/**
 * The selection's frame on the board: a rectangle around what is selected, eight resize
 * handles (`data-handle`: `nw`, `n`…) and its four sides, which resize too (a band along each) and a round handle above it that turns it
 * (`data-rotate`). Drawn in screen pixels over the layer, so they keep their size whatever the
 * zoom. Also the snapping guides of a move in progress and the marquee being drawn.
 */
import { For, Show, type JSX } from "solid-js";
import { useApp } from "../../../app/context";
import { HANDLES, bounds, normal, type Rect } from "../../../state/board";

/** The selection's handles, the guides and the marquee (`marquee` in board units). */
export function Handles(props: { marquee: Rect | null; hidden: boolean }): JSX.Element {
  const { board } = useApp();
  /** The selection's bounds on screen. */
  const box = () => {
    const places = board.content().places;
    const b = bounds(board.selected().flatMap((id) => (places.has(id) ? [places.get(id)!] : [])));
    if (!b) return null;
    const c = board.camera();
    return { x: b.x * c.k + c.x, y: b.y * c.k + c.y, w: b.w * c.k, h: b.h * c.k };
  };
  /** One item selected and turned: the frame turns with it. */
  const turned = () => {
    const ids = board.selected();
    return ids.length === 1 ? (board.content().places.get(ids[0]!)?.rotate ?? 0) : 0;
  };
  /** Where a handle sits on the frame (fractions of its width and height). */
  const spot = (h: string) => ({ x: h.includes("w") ? 0 : h.includes("e") ? 1 : 0.5, y: h.includes("n") ? 0 : h.includes("s") ? 1 : 0.5 });
  const marquee = () => {
    if (!props.marquee) return null;
    const m = normal(props.marquee);
    const c = board.camera();
    return { x: m.x * c.k + c.x, y: m.y * c.k + c.y, w: m.w * c.k, h: m.h * c.k };
  };
  return (
    <div class="board-overlay">
      <Show when={!props.hidden && box()}>
        {(b) => (
          <div class="board-selection" style={{ left: `${b().x}px`, top: `${b().y}px`, width: `${b().w}px`, height: `${b().h}px`, transform: turned() ? `rotate(${turned()}deg)` : undefined }}>
            <For each={["n", "e", "s", "w"]}>{(side) => <span class={`board-edge edge-${side}`} data-handle={side} />}</For>
            <For each={HANDLES}>{(h) => <span class={`board-handle h-${h}`} data-handle={h} style={{ left: `${spot(h).x * 100}%`, top: `${spot(h).y * 100}%` }} />}</For>
            <span class="board-rotate" data-rotate="yes" title="Drag to turn (Shift: by 15°)" />
          </div>
        )}
      </Show>
      <For each={board.guides()}>
        {(g) => {
          const c = board.camera();
          return "x" in g ? <div class="board-guide v" style={{ left: `${g.x * c.k + c.x}px` }} /> : <div class="board-guide h" style={{ top: `${g.y * c.k + c.y}px` }} />;
        }}
      </For>
      <Show when={marquee()}>{(m) => <div class="board-marquee" id="board-marquee" style={{ left: `${m().x}px`, top: `${m().y}px`, width: `${m().w}px`, height: `${m().h}px` }} />}</Show>
    </div>
  );
}
